import React, { useState, useCallback, useRef } from 'react';
import DataGrid, {
    Column,
    Editing,
    Popup,
    Form,
    Toolbar, Item as ToolbarItem,
    HeaderFilter,
    FilterRow,
    Lookup, Button as DxButton,
    DataGridTypes } from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/form';
import { RequiredRule } from 'devextreme-react/form';
import { Popup as DxPopup, ToolbarItem as PopupToolbarItem } from 'devextreme-react/popup';
import { WorkOrderPhotos } from '../work-order-photos/WorkOrderPhotos';
import { withLoadPanel } from '../../../utils/withLoadPanel';
import './WorkOrderTable.scss';
import DataSource from 'devextreme/data/data_source';
import Guid from 'devextreme/core/guid';
import 'devextreme/data/odata/store';
import axios from 'axios';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../../contexts/auth';
import moment from 'moment';
import ODataStore from 'devextreme/data/odata/store';
const Grid = ({ workTaskOid }: { workTaskOid: string }) => {
const { user } = useAuth();
const gridRef = useRef<DataGrid>(null);
const [usedMaterials, setUsedMaterials] = useState<any[]>([]);
const [photosPopupOpen, setPhotosPopupOpen] = useState(false);
const [photosWorkOrderOid, setPhotosWorkOrderOid] = useState<string | null>(null);
const openPhotos = useCallback((oid: string) => {
  setPhotosWorkOrderOid(oid);
  setPhotosPopupOpen(true);
}, []);
const closePhotosPopup = useCallback(() => setPhotosPopupOpen(false), []);
const closePhotosOptions = { text: 'Sluiten', onClick: closePhotosPopup };
const dataSource = new DataSource({
        store: {
            type: 'odata',
            version: 4,
            key: 'Oid',
            keyType: 'Guid',
            url: `${process.env.REACT_APP_URL}/api/odata/WorkOrder`,
            beforeSend: (e) => {
                e.headers = {
                    'Authorization': `Bearer ${user?.token}`
                };
            },
            onLoaded: function(result) {
              result.forEach((value) => {
                value.ArrivalTime = moment(value.ArrivalTime, 'HH:mm').toDate();
                value.DepartureTime = moment(value.DepartureTime, 'HH:mm').toDate();
              });
          },
        },
        paginate: false,
        select: [
            'Oid',
            'WorkOrderDate',
            'HasUsedMaterials',
            'PerformedTasks',
            'Done',
            'ArrivalTime',
            'DepartureTime',
            'WorkedHours',
            'CreatedBy',
            'CreatedAt',
            'UpdatedAt',
            'DrovenKm',
            'WorkTask'
        ],
        filter: ['WorkTask/Oid', '=', new Guid(workTaskOid)],
        expand: ['WorkTask', 'CreatedBy', 'UsedMaterials'],
  });
  const employeesDataSource = new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/Employee`,
    version: 4,
    beforeSend: function(e) {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  });

  const addMaterial = useCallback(() => {
    setUsedMaterials(prev => [...prev, { Name: '', Quantity: 1 }]);
  }, []);

  const removeMaterial = useCallback((index: number) => {
    setUsedMaterials(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateMaterial = useCallback((index: number, field: string, value: any) => {
    setUsedMaterials(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  }, []);

  const timeEditorOptions = { displayFormat : 'HH:mm', type: 'time', defaultValue : new Date() };
  const dateEditorOptions = { defaultValue : new Date() };
  const createdByOptions = { dataSource: employeesDataSource, valueExpr: 'Oid', displayExpr: 'FullName' };

  const onSaving = useCallback((e: DataGridTypes.SavingEvent) => {
    const insertChange = e.changes.find(c => c.type === 'insert');
    const updateChange = e.changes.find(c => c.type === 'update');
    if (insertChange) {
      e.cancel = true;
      const data = insertChange.data;
      axios.post('/api/WorkOrder/Create', {
        WorkOrderDate: data.WorkOrderDate,
        PerformedTasks: data.PerformedTasks,
        DrovenKm: data.DrovenKm ?? 0,
        Done: data.Done ?? false,
        WorkTaskOid: workTaskOid,
        CreatedByOid: data.CreatedBy?.Oid?._value?.toString() ?? data.CreatedBy?.Oid?.toString(),
        ArrivalTimeString: data.ArrivalTime ? moment(data.ArrivalTime).format('HH:mm') : undefined,
        DepartureTimeString: data.DepartureTime ? moment(data.DepartureTime).format('HH:mm') : undefined,
        UsedMaterials: usedMaterials.filter((m: any) => m.Name?.trim()).map((m: any) => ({ Name: m.Name, Quantity: m.Quantity })),
      }, {
        headers: { 'Authorization': `Bearer ${user?.token}` }
      }).then(() => {
        e.component.cancelEditData();
        e.component.refresh();
      }).catch(() => {
        notify('Er is een fout opgetreden bij het opslaan van de werkbon', 'error', 1000);
      });
    } else if (updateChange) {
      e.cancel = true;
      const key = updateChange.key?._value?.toString() ?? updateChange.key?.toString();
      const data = updateChange.data;
      axios.put(`/api/WorkOrder/Update/${key}`, {
        WorkOrderDate: data.WorkOrderDate,
        PerformedTasks: data.PerformedTasks,
        DrovenKm: data.DrovenKm,
        Done: data.Done,
        CreatedByOid: data.CreatedBy?.Oid?._value?.toString() ?? data.CreatedBy?.Oid?.toString(),
        ArrivalTimeString: data.ArrivalTime ? moment(data.ArrivalTime).format('HH:mm') : undefined,
        DepartureTimeString: data.DepartureTime ? moment(data.DepartureTime).format('HH:mm') : undefined,
        UsedMaterials: usedMaterials.filter((m: any) => m.Name?.trim()).map((m: any) => ({ Name: m.Name, Quantity: m.Quantity })),
      }, {
        headers: { 'Authorization': `Bearer ${user?.token}` }
      }).then(() => {
        e.component.cancelEditData();
        e.component.refresh();
      }).catch(() => {
        notify('Er is een fout opgetreden bij het opslaan van de werkbon', 'error', 1000);
      });
    }

    const removeChange = e.changes.find(c => c.type === 'remove');
    if (removeChange) {
      e.cancel = true;
      const key = removeChange.key?._value?.toString() ?? removeChange.key?.toString();
      axios.delete(`/api/WorkOrder/${key}`, {
        headers: { 'Authorization': `Bearer ${user?.token}` }
      }).then(() => {
        e.component.cancelEditData();
        e.component.refresh();
        notify('Werkbon verwijderd', 'success', 1000);
      }).catch(() => {
        notify('Er is een fout opgetreden bij het verwijderen van de werkbon', 'error', 1000);
      });
    }
  }, [user?.token, workTaskOid]);

  return (
    <>
    <DataGrid
      ref={gridRef}
      className='tasks-grid'
      dataSource={dataSource}
      onEditingStart={(e) => { setUsedMaterials((e.data.UsedMaterials || []).map((m: any) => ({ Name: m.Name, Quantity: m.Quantity }))); }}
      onInitNewRow={() => { setUsedMaterials([]); }}
      onSaving={onSaving}
      columnAutoWidth>
      <Toolbar>
          <ToolbarItem name='addRowButton' />
      </Toolbar>
      <HeaderFilter visible allowSearch />
      <FilterRow visible />
      <Editing
        mode='popup'
        allowUpdating
        allowAdding
        allowDeleting>
            <Popup title='Werkbon' showTitle width={700} height={650} />
            <Form>
                <Item itemType='group' colCount={2} colSpan={2}>
                <Item dataField='WorkOrderDate' editorOptions={dateEditorOptions} />
                <Item dataField='Done' editorType='dxCheckBox' />
                <Item dataField='CreatedBy.Oid' caption='Uitgevoerd door' editorType='dxLookup' editorOptions={createdByOptions} />
                <Item dataField='DrovenKm' />
                <Item dataField='ArrivalTime' editorOptions={timeEditorOptions} />
                <Item dataField='DepartureTime' editorOptions={timeEditorOptions} />
                <Item
                  dataField='PerformedTasks'
                  caption='Uitgevoerde werkzaamheden'
                  editorType='dxTextArea'
                  colSpan={2}
                  editorOptions={{ height: 200 }} />
               </Item>
                 <Item itemType='group' caption='Materiaal' colSpan={2}>
                  <div className='materials-editor'>
                    {usedMaterials.map((m, idx) => (
                      <div key={idx} className='material-row'>
                        <input
                          className='material-input material-name'
                          value={m.Name || ''}
                          onChange={(e) => updateMaterial(idx, 'Name', e.target.value)}
                          placeholder='Naam'
                        />
                        <input
                          className='material-input material-qty'
                          type='number'
                          value={m.Quantity ?? 1}
                          onChange={(e) => updateMaterial(idx, 'Quantity', parseInt(e.target.value) || 0)}
                          min={1}
                          placeholder='Aantal'
                        />
                        <button className='material-remove-btn' type='button' onClick={() => removeMaterial(idx)}>
                          <i className='dx-icon dx-icon-trash' />
                        </button>
                      </div>
                    ))}
                    <button className='material-add-btn' type='button' onClick={addMaterial}>
                      <i className='dx-icon dx-icon-add' />
                      <span>Materiaal toevoegen</span>
                    </button>
                  </div>
                </Item>
            </Form>
          </Editing>
        <Column visible={false} dataField='PerformedTasks' caption='Uitgevoerde werkzaamheden' />
        <Column visible={false} dataField='DrovenKm' caption='Gereden km' />
        <Column
        dataField='WorkOrderDate'
        sortOrder='desc'
        format='dd-MM-yyyy'
        dataType='date'
        caption='Uitvoerdatum'>
        <RequiredRule message='Uitvoerdatum ontbreekt!' />
        </Column>
        <Column
        dataField='ArrivalTime'
        dataType='datetime'
        format='HH:mm'
        caption='Aankomst tijd'>
        <RequiredRule message='Aankomst tijd ontbreekt!' />
        </Column>
        <Column
        dataField='DepartureTime'
        dataType='datetime'
        format='HH:mm'
        caption='Vertrek tijd'>
        <RequiredRule message='Vertrek tijd ontbreekt!' />
        </Column>
        <Column
        dataField='CreatedBy.Oid'
        caption='Medewerker'>
        <Lookup
          dataSource={employeesDataSource}
          valueExpr='Oid'
          displayExpr='FullName' />
       <RequiredRule message='Selecteer een medeweerker!' />
    </Column>
    <Column
        dataField='WorkedHours'
        caption='Gewerkte uren' />
    <Column
        caption='Materiaal'
        dataField='HasUsedMaterials' />
    <Column
        caption='Factureerd'
        dataField='Done' />
    <Column type='buttons' caption='Acties' width={140}>
      <DxButton name='edit' />
      <DxButton name='delete' />
      <DxButton
        hint="Foto's"
        icon='image'
        onClick={(e) => openPhotos(e.row?.data?.Oid)}
      />
    </Column>
    </DataGrid>
    <DxPopup
      visible={photosPopupOpen}
      onHiding={closePhotosPopup}
      dragEnabled={false}
      hideOnOutsideClick
      showTitle
      title="Foto's"
      width={800}
      height={600}>
      <PopupToolbarItem widget='dxButton' toolbar='bottom' location='center' options={closePhotosOptions} />
      {photosWorkOrderOid && <WorkOrderPhotos workOrderOid={photosWorkOrderOid} />}
    </DxPopup>
    </>
  );
};

const GridWithLoadPanel = withLoadPanel(Grid);
export const WorkOrderTable = ({ workTaskOid, isLoading }: { workTaskOid?: string, isLoading: boolean }) => {
  return (
    <div className='card-tasks'>
          <GridWithLoadPanel
        workTaskOid={workTaskOid}
        hasData={!!workTaskOid}
        loading={isLoading}
        panelProps={{
            container: '.card-tasks',
            position: { of: '.card-tasks' }
        }}
      />
    </div>
  );
};
